import {FC, FocusEvent, useState} from 'react';
import {toast} from 'react-toastify';
//mui
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material';
//store
import {getAttendee, getAttendeeSelector} from '@/store/organization-attendees';
import {useAppDispatch, useAppSelector} from '@/store/hooks';
//services
import {OrganizationTableAndTicketsService} from '@/utils/services';
//constants
import {
  BID,
  BODY2,
  CHECKED_IN,
  CHECKED_IN_DASH,
  CONTACT_PHONE,
  DOLLAR,
  HASH,
  NUMBER,
  PRICE,
  RIGHT,
  SUBTITLE2,
  TABLE,
  TABLE_PURCHASER,
  TYPE_OF_SEAT,
} from '@/utils/constants';
//enums
import {ATTENDEE_STATUS, ROUTER_URL} from '@/utils/enums';
//components
import Input from '@/shared/components/Inputs/Input';
//icons
import {ReactComponent as EllipseIcon} from '@/assets/icons/ellipse-green.svg';
import {ReactComponent as TickIcon} from '@/assets/icons/tick.svg';
//helpers
import {formatTablePhoneNumber, getErrorMessage, getTableStatusStyle} from '@/utils/helpers';
//styles
import {attendeeTablePurchaserStyle} from './AttendeeTablePurchaser.styles';
import Modal from '@/shared/components/Modal';
import TableDetails from '@/components/Organization/TablesAndTickets/TableDetails';
import {tablesAndTicketsTableStyle} from '@/components/Organization/TablesAndTickets/TablesAndTicketsTable/TablesAndTicketsTable.styles';
import {currencyFormatter} from '@/utils/helpers/currency';
import {useNavigate} from 'react-router-dom';
import {useApiInstance} from '@/contexts/ApiInstanceContext';

const AttendeeTablePurchaser: FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const {apiInstance} = useApiInstance();

  const [isShowTicketDetailsModal, setIsShowTicketDetailsModal] = useState<boolean>(false);
  const [detailsTicketId, setDetailsTicketId] = useState<number>();

  const attendee = useAppSelector(getAttendeeSelector);
  const isHover = false;

  const isLaptop = useMediaQuery('(min-width:1200px)');

  const handleUpdateTableNumber = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>, tableId: number) => {
    const {value} = event.target;
    setTableNumber(value, tableId);
  };

  const handleOpenTicketDetailsModal = (tableId?: number) => {
    if (isShowTicketDetailsModal) {
      getAttendeeData();
    }

    setIsShowTicketDetailsModal(!isShowTicketDetailsModal);
    if (!isShowTicketDetailsModal && tableId) setDetailsTicketId(tableId!);
    // if (!tableId) getTicketsListData();
  };

  const handleTicketDetailsNavigate = (tableId: number) => {
    navigate(`${ROUTER_URL.ORGANIZATION_TABLES_DETAILS}/${tableId}`);
  };

  const setTableNumber = async (newNumber: string, tableId: number) => {
    try {
      const reqBody = {
        id: tableId,
        number: newNumber,
      };
      await OrganizationTableAndTicketsService.updateTableNumber(reqBody, apiInstance);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      getAttendeeData();
    }
  };

  const getAttendeeData = async () => {
    try {
      await dispatch(getAttendee(attendee!.id)).unwrap();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  return (
    <Box>
      <Box sx={attendeeTablePurchaserStyle.tableContent}>
        <TableContainer sx={attendeeTablePurchaserStyle.container}>
          <Table sx={attendeeTablePurchaserStyle.table}>
            <TableHead>
              <TableRow sx={attendeeTablePurchaserStyle.tableHeader}>
                <TableCell>
                  {BID}
                  {HASH}
                </TableCell>
                <TableCell>
                  {TABLE}
                  {HASH}
                </TableCell>
                <TableCell>{TYPE_OF_SEAT}</TableCell>
                <TableCell>{TABLE_PURCHASER}</TableCell>
                <TableCell>{CONTACT_PHONE}</TableCell>
                <TableCell align={RIGHT}>{PRICE}</TableCell>
                <TableCell align={RIGHT}>{CHECKED_IN} ?</TableCell>
                <TableCell>
                  {TABLE}
                  {HASH}
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              <TableRow key={attendee?.id} sx={attendeeTablePurchaserStyle.tableBody}>
                <TableCell>
                  <Typography variant={SUBTITLE2}>{attendee?.id}</Typography>
                </TableCell>

                <TableCell>
                  <Typography variant={SUBTITLE2}>{attendee?.table?.number}</Typography>
                </TableCell>

                <TableCell>
                  <Typography
                    variant={SUBTITLE2}
                    onClick={
                      isLaptop
                        ? () => handleOpenTicketDetailsModal(attendee?.table.id)
                        : () => handleTicketDetailsNavigate(attendee?.table.id as number)
                    }
                  >
                    {attendee?.table?.name}
                  </Typography>
                  <Typography variant={BODY2}>{attendee?.table?.tableInfo.tier}</Typography>
                </TableCell>

                <TableCell>
                  <Box>
                    <Typography variant={SUBTITLE2}>{attendee?.table?.purchaser?.firstName}</Typography>
                    <Typography variant={SUBTITLE2}>{attendee?.table?.purchaser?.lastName}</Typography>
                  </Box>
                  <Typography variant={BODY2}>{attendee?.table?.purchaser?.email}</Typography>
                </TableCell>

                <TableCell>{formatTablePhoneNumber(attendee?.table?.purchaser?.phoneNumber)}</TableCell>

                <TableCell align={RIGHT}>{currencyFormatter.format(attendee?.table?.tableInfo.price || 0)}</TableCell>

                <TableCell align={RIGHT}>
                  <Typography sx={getTableStatusStyle(attendee?.status as string, isHover)}>
                    {attendee?.status === ATTENDEE_STATUS.PENDING
                      ? 'No'
                      : ATTENDEE_STATUS.CHECKED_IN.replace(/\bCheckedIn\b/g, CHECKED_IN_DASH)}
                    {attendee?.status === ATTENDEE_STATUS.PENDING ? (
                      <Box sx={attendeeTablePurchaserStyle.pendingStatus}>
                        <EllipseIcon />
                      </Box>
                    ) : (
                      <TickIcon
                        style={{
                          width: '8px',
                          height: '8px',
                          stroke: 'var(--success-600)',
                        }}
                      />
                    )}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Input
                    defaultValue={attendee?.table?.number}
                    onBlur={(event) => handleUpdateTableNumber(event, attendee?.table?.id as number)}
                    type={NUMBER}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <Modal
        onClose={handleOpenTicketDetailsModal}
        open={isShowTicketDetailsModal}
        sx={tablesAndTicketsTableStyle.modal}
      >
        <TableDetails onCloseModal={handleOpenTicketDetailsModal} currentTableId={detailsTicketId} />
      </Modal>
    </Box>
  );
};

export default AttendeeTablePurchaser;
